import { DOCUMENT, formatDate } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { ToastService } from '../_core/toast.service';
import { AnalyticsChoice, ConsentStore } from './consent.store';
import { MapsChoice, MapsConsentStore } from './maps-consent.store';
import { ContentsquareService } from './contentsquare.service';

export type ConsentBannerMode = 'first-visit' | 'settings';

/**
 * Story 50.6 — asks for, and later changes, the analytics-cookie choice.
 *
 * Rendered first in `AppComponent` so keyboard and screen-reader users meet it before the page,
 * and drawn as a fixed card at the bottom that never blocks the page: no backdrop, no focus trap,
 * no scroll lock (the site stays usable without a choice). Accept and Reject share one style:
 * refusing must be as easy and as visible as accepting.
 *
 * It renders nothing on the server and on the hydrating client's first pass (the choice lives in
 * the browser), so server HTML and hydration never disagree. It does not appear where no
 * analytics can run anyway — token pages, the sign-in callback, super-admin pages — and only in
 * builds that have a tag ID.
 */
@Component({
  selector: 'app-consent-banner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink],
  templateUrl: './consent-banner.component.html',
  styleUrl: './consent-banner.component.scss',
  // On the host, not `document`: Escape pressed inside the banner bubbles here; one meant for
  // another dialog on the page never does.
  host: { '(keydown.escape)': 'onEscape()' },
})
export class ConsentBannerComponent {
  protected readonly consent = inject(ConsentStore);
  /** 87.7b (S7b AC 4, J6): "Cookie settings" gains Maps from Google wherever a map can load, and nowhere else. */
  protected readonly maps = inject(MapsConsentStore);
  private readonly contentsquare = inject(ContentsquareService);
  private readonly toast = inject(ToastService);
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);

  private readonly title = viewChild<ElementRef<HTMLElement>>('title');
  private readonly rendered = signal(false);
  /** The control that opened "Cookie settings"; focus returns to it when they close. */
  private returnFocusTo: HTMLElement | null = null;

  readonly mode = computed<ConsentBannerMode | null>(() => {
    if (!this.rendered()) return null;
    // where Maps does not apply this is the 50.6 rule unchanged (J6); the first visit stays the analytics question alone
    const analytics = this.consent.configured() && this.consent.loaded();
    if (this.consent.settingsOpen() && (analytics || this.maps.applies())) return 'settings';
    if (analytics && this.consent.choice() === null && !this.contentsquare.excludedNow()) return 'first-visit';
    return null;
  });

  /** The current choice, shown in "Cookie settings". */
  readonly status = computed(() => {
    const stored = this.consent.stored();
    if (stored !== null) {
      const date = formatDate(stored.at, 'd MMMM y', 'en-US');
      return stored.analytics === 'granted'
        ? `You accepted analytics cookies on ${date}.`
        : `You rejected analytics cookies on ${date}.`;
    }
    if (this.consent.gpc()) return 'Your browser sends a Global Privacy Control signal, so analytics cookies are off.';
    return 'You have not made a choice yet.';
  });

  /** 87.7b (M4): the Maps choice, shown in "Cookie settings" where Maps applies. */
  readonly mapsStatus = computed(() => {
    if (this.maps.withdrawalUnsaved()) return 'Your browser could not save this choice, so maps stay off on this page.';
    const stored = this.maps.stored();
    if (stored === null) return 'You have not made a choice yet. Maps stay off until you turn them on.';
    const date = formatDate(stored.at, 'd MMMM y', 'en-US');
    return stored.maps === 'granted' ? `You turned maps on on ${date}.` : `You turned maps off on ${date}.`;
  });

  constructor() {
    afterNextRender(() => {
      this.maps.start();
      this.rendered.set(true);
    });
    effect(() => {
      if (this.mode() !== 'settings') return;
      untracked(() => {
        const active = this.document.activeElement;
        this.returnFocusTo = active instanceof HTMLElement && active !== this.document.body ? active : null;
        afterNextRender(() => this.title()?.nativeElement.focus(), { injector: this.injector });
      });
    });
  }

  choose(choice: AnalyticsChoice): void {
    const fromSettings = this.mode() === 'settings';
    this.consent.choose(choice);
    this.toast.info(
      choice === 'granted'
        ? 'Analytics cookies accepted. You can change your choice at any time on our Cookie Policy page.'
        : 'Analytics cookies rejected. You can change your choice at any time on our Cookie Policy page.',
    );
    if (fromSettings) this.restoreFocus();
  }

  /** 87.7b (J3): turning Maps off removes every map at once, here and in the other tabs (the store's record). */
  chooseMaps(choice: MapsChoice): void {
    this.maps.choose(choice);
    this.toast.info(choice === 'granted' ? 'Maps from Google turned on.' : 'Maps from Google turned off.');
  }

  close(): void {
    this.consent.closeSettings();
    this.restoreFocus();
  }

  protected onEscape(): void {
    if (this.mode() === 'settings') this.close();
  }

  private restoreFocus(): void {
    const target = this.returnFocusTo;
    this.returnFocusTo = null;
    if (target?.isConnected) target.focus();
  }
}
